/**
 * Audit-id → name resolution (task 999).
 *
 * Report: `project/reports/innovate-app-user-attribution-names-not-ids.md`.
 *
 * Every innovate-service resource stamps `createdBy` / `updatedBy` /
 * `deletedBy` / `publishedBy` with the caller's AMS user id, and the API
 * returns that id and nothing else — innovate-service has no table of AMS users
 * to join against. The names the app needs already reach the browser one other
 * way: `GET /api/account/members` (the AMS members proxy behind
 * `useTeamMembers`) carries `{ userId, displayName, email, role }` for every
 * member of the caller's account.
 *
 * This hook is the join, done app-side. It wraps that one query — already in
 * React Query with a 60s stale time — so the seven audit sites cost one request
 * per account, not one per id, and never hit the network again on a page that
 * has loaded any of them once.
 *
 * Callers should use `<UserName userId={…} />` rather than calling `resolve`
 * directly; the component owns the loading skeleton that keeps a UUID from
 * flashing before the member list lands.
 */

import { useCallback } from 'react';
import { useTeamMembers } from './useTeam';

/**
 * The platform's own user id: service-key and public callers, seeds, and
 * platform-owned collaborations are all stamped with it.
 *
 * A fixed protocol constant shared with innovate-service — it is the same zero
 * UUID in every environment, so it is deliberately NOT config and NOT an env
 * var. A value that never varies by environment does not belong in
 * `src/config`.
 */
export const SYSTEM_USER_ID = '00000000-0000-0000-0000-000000000000';

/** Label for actions the platform took on its own behalf. */
export const SYSTEM_USER_LABEL = 'Innovate';

/** Label for an id the account's member list no longer contains. */
export const FORMER_MEMBER_LABEL = 'Former member';

/** What an id resolved to, and which of the three cases produced it. */
export interface ResolvedUserName {
  name: string;
  kind: 'member' | 'system' | 'former';
}

export interface UseUserNamesResult {
  /**
   * Resolve one audit user id to a display name.
   *
   * Returns `null` while the member list is still loading, so the caller can
   * render a skeleton instead of the raw UUID.
   */
  resolve: (userId: string) => ResolvedUserName | null;
  /** True while the member list is in flight. */
  isLoading: boolean;
}

export function useUserNames(): UseUserNamesResult {
  const { data: members, isLoading, isError } = useTeamMembers();

  const resolve = useCallback(
    (userId: string): ResolvedUserName | null => {
      // The platform is the platform whether or not the member list ever
      // arrives — this case needs no lookup and is answered first.
      if (userId === SYSTEM_USER_ID) {
        return { name: SYSTEM_USER_LABEL, kind: 'system' };
      }

      if (isLoading) return null;

      // The members proxy failed (it is gone, or the request errored). Falling
      // back to the id is worse copy but a true statement; calling every id a
      // former member would be a lie, and hiding the line would regress below
      // what the app shows today. See the report, "Where the member list is
      // not available".
      if (isError || !members) {
        return { name: userId, kind: 'former' };
      }

      const member = members.find((candidate) => candidate.userId === userId);
      if (member) {
        // Same derivation chain as the profile report: displayName first, the
        // email local part when AMS has no name for the User yet.
        return {
          name: member.displayName || member.email.split('@')[0],
          kind: 'member',
        };
      }

      // A UUID the account's member list does not contain: a User who has left.
      // A cross-account id cannot occur here — every resource the app lists
      // belongs to the caller's own account.
      return { name: FORMER_MEMBER_LABEL, kind: 'former' };
    },
    [members, isLoading, isError],
  );

  return { resolve, isLoading };
}

export default useUserNames;
